/**
 * Envoltorio de MediaRecorder para capturar la respuesta del niño. Expone
 * start/stop y entrega un Blob de audio. Aísla el acceso a getUserMedia para
 * que las pantallas no toquen APIs del navegador directamente.
 *
 * Auto-stop (F1): si `iniciar` recibe una ConfigVoz, se analiza el nivel del
 * micrófono en tiempo real y se invoca `onCorte` al detectar el fin de la
 * emisión o al agotar el tiempo máximo. La política vive en vad.ts (pura).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  debeCortar,
  medirNivel,
  siguienteEstado,
  ESTADO_VOZ_INICIAL,
  type ConfigVoz,
} from "./vad.js";

function pickMimeType(): string {
  const candidatos = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  const MR = (globalThis as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
  if (MR && typeof MR.isTypeSupported === "function") {
    for (const c of candidatos) if (MR.isTypeSupported(c)) return c;
  }
  return "audio/webm";
}

export interface OpcionesGrabacion {
  /** Cierre automático por detección de voz o tiempo (solo F1). */
  autoStop?: ConfigVoz;
  /** Se invoca cuando el auto-stop decide cortar; el llamador hace `detener`. */
  onCorte?: () => void;
}

export interface Recorder {
  soportado: boolean;
  grabando: boolean;
  contentType: string;
  iniciar: (opts?: OpcionesGrabacion) => Promise<void>;
  detener: () => Promise<Blob>;
}

const INTERVALO_ANALISIS_MS = 100;

export function useRecorder(): Recorder {
  const [grabando, setGrabando] = useState(false);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);
  const contentType = useRef(pickMimeType());

  const soportado =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof (globalThis as { MediaRecorder?: unknown }).MediaRecorder !== "undefined";

  const pararAnalisis = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = null;
    void audioCtxRef.current?.close();
    audioCtxRef.current = null;
  }, []);

  /** Vigila el nivel del micrófono y avisa cuando toca cortar (F1). */
  const vigilarVoz = useCallback(
    (stream: MediaStream, cfg: ConfigVoz, onCorte: () => void) => {
      const Ctx = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
      if (!Ctx) {
        // Sin WebAudio nos queda solo el corte por tiempo máximo.
        timerRef.current = window.setTimeout(onCorte, cfg.maxMs);
        return;
      }
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);

      let estado = ESTADO_VOZ_INICIAL;
      timerRef.current = window.setInterval(() => {
        estado = siguienteEstado(estado, medirNivel(analyser), INTERVALO_ANALISIS_MS);
        if (debeCortar(estado, cfg)) {
          pararAnalisis();
          onCorte();
        }
      }, INTERVALO_ANALISIS_MS);
    },
    [pararAnalisis],
  );

  const iniciar = useCallback(
    async (opts?: OpcionesGrabacion) => {
      if (!soportado) throw new Error("Este dispositivo no soporta grabación de audio");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: contentType.current });
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRef.current = mr;
      mr.start();
      setGrabando(true);
      if (opts?.autoStop && opts.onCorte) vigilarVoz(stream, opts.autoStop, opts.onCorte);
    },
    [soportado, vigilarVoz],
  );

  const detener = useCallback(async () => {
    pararAnalisis();
    const mr = mediaRef.current;
    if (!mr) throw new Error("no hay grabación activa");
    const blob = await new Promise<Blob>((resolve) => {
      mr.onstop = () => resolve(new Blob(chunksRef.current, { type: contentType.current }));
      mr.stop();
    });
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mediaRef.current = null;
    setGrabando(false);
    return blob;
  }, [pararAnalisis]);

  // Limpieza: soltar el micrófono si el componente se desmonta grabando.
  useEffect(() => {
    return () => {
      pararAnalisis();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [pararAnalisis]);

  return { soportado, grabando, contentType: contentType.current, iniciar, detener };
}
